import type { ReactNode } from 'react'
import { Link, Navigate, useLocation, useParams } from 'react-router-dom'

import { Card, EmptyState, LoadingBlock, SectionHeader } from '@/design'
import { useFilterSearch } from '@/lib/useGlobalFilters'

import { ACCESS_UNKNOWN_TITLE, usePageAccess, type AccessFailure } from './pageAccess'
import type { RouteDef, SectionDef } from './routes'

/** The section's tabs the caller's role does not refuse (`tabsOpenTo`). */
export function useSectionTabs(section: SectionDef) {
  const { visibleTabs } = usePageAccess()
  return visibleTabs(section).map((tab) => ({ label: tab.label, to: tab.path, end: tab.end }))
}

/** A page inside a section: its title block and tabs, then its content. */
export function SectionFrame({
  route,
  title,
  description,
  actions,
  children,
}: {
  route: RouteDef
  title?: string
  description?: ReactNode
  actions?: ReactNode
  children: ReactNode
}) {
  const tabs = useSectionTabs(route.section)
  const isSectionPage = route.section.tabs.length > 0
  return (
    <>
      <SectionHeader
        eyebrow={isSectionPage ? route.section.label : undefined}
        title={title ?? route.title}
        description={description ?? route.description ?? route.section.description}
        actions={actions}
        tabs={tabs}
        activeTab={route.tab}
      />
      {children}
    </>
  )
}

/**
 * Every route in the IA renders at least this: a titled page with its
 * section's tabs, saying plainly that the page arrives later. Entity routes
 * show the entity they were opened for, so drill-down links can be followed
 * end to end today.
 */
export function PlaceholderPage({ route }: { route: RouteDef }) {
  const params = useParams()
  const search = useFilterSearch()
  const entity = Object.values(params).find(Boolean)
  return (
    <SectionFrame route={route}>
      <Card>
        {entity ? (
          <p className="ci-placeholder__entity">
            Opened for <code>{entity}</code>
          </p>
        ) : null}
        <EmptyState
          variant="no-data"
          title={`${route.title} is coming soon`}
          description="This page is part of the Competition Insights layout and arrives in a follow-up release. Links and filters that lead here already work."
          action={
            <Link className="ci-button" to={{ pathname: '/', search }}>
              Back to Overview
            </Link>
          }
        />
      </Card>
    </SectionFrame>
  )
}

export function RedirectPage({ route }: { route: RouteDef }) {
  const { search } = useLocation()
  return <Navigate to={{ pathname: route.redirectTo ?? '/', search }} replace />
}

/**
 * Refuses a route outside the tenant's plan or the caller's role, with the
 * reason. Navigation already hides these; this covers a typed or shared URL.
 * The decision is `pageAccess`'s; this renders each `PageState`.
 *
 * A plan or session read blocks only what it is needed for: a page in no
 * flag-gated module, or needing no permission, neither waits on it nor fails
 * with it. Where it was needed and failed with nothing read, access is unknown
 * — never granted — and a failure that may pass offers a retry; a refusal does
 * not, as asking again cannot change it (`isRetryableError`). An expired
 * session goes on to the signed-out page (`useSessionExpiry`).
 */
export function RouteGate({ route, children }: { route: RouteDef; children: ReactNode }) {
  const { pageAccess } = usePageAccess()
  const { section } = route
  const access = pageAccess(section, route)

  switch (access.state) {
    case 'pending':
      return <LoadingBlock rows={4} label="Checking access" />
    case 'forbidden':
      return (
        <>
          <SectionHeader eyebrow={section.label} title={route.title} />
          <Card>
            <EmptyState variant="permission" />
          </Card>
        </>
      )
    case 'disabled':
      return (
        <>
          <SectionHeader eyebrow={section.label} title={route.title} />
          <Card>
            <EmptyState
              variant="permission"
              title="Not included in your plan"
              description={`${section.label} is a module this organisation does not have. An Owner can ask TmT to enable it.`}
            />
          </Card>
        </>
      )
    case 'unknown':
      return <AccessUnknown route={route} failure={access.failure} />
    case 'open':
      return <>{children}</>
  }
}

/**
 * The page could not be checked, so it is not shown. With `failure.retry` the
 * read that failed may pass, and a success opens the page in place. The button
 * stays focusable while it retries (`aria-disabled`, not `disabled`), and a
 * retry that fails again changes the announced text.
 */
function AccessUnknown({ route, failure }: { route: RouteDef; failure: AccessFailure }) {
  const { retrying, retryFailed, retry } = failure
  return (
    <>
      <SectionHeader eyebrow={route.section.label} title={route.title} />
      <Card>
        <EmptyState
          variant="outage"
          title={ACCESS_UNKNOWN_TITLE}
          description={`We ${retryFailed ? 'still ' : ''}could not confirm your access to this page. Try again shortly.`}
          announce
          action={
            retry ? (
              <button
                type="button"
                className="ci-button"
                aria-disabled={retrying || undefined}
                onClick={retrying ? undefined : retry}
              >
                {retrying ? 'Trying again…' : 'Try again'}
              </button>
            ) : undefined
          }
        />
      </Card>
    </>
  )
}

export function NotFoundPage() {
  const search = useFilterSearch()
  return (
    <>
      <SectionHeader title="Page not found" />
      <Card>
        <EmptyState
          variant="no-results"
          title="There is no page at this address"
          description="It may have moved in the new layout."
          action={
            <Link className="ci-button" to={{ pathname: '/', search }}>
              Go to Overview
            </Link>
          }
        />
      </Card>
    </>
  )
}
